import { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api";

type UseCalendarMembershipResult = {
  isLoading: boolean;
  isMember: boolean;
  refetch: () => Promise<void>;
};

/**
 * Hook to check if the current user is a member of the Cal.com team.
 * Returns isMember: true if the user's email is in the team.
 *
 * @param enabled - When false, skips the fetch entirely (defaults to true).
 */
export function useCalendarMembership(enabled = true): UseCalendarMembershipResult {
  const [isLoading, setIsLoading] = useState(enabled);
  const [isMember, setIsMember] = useState(false);

  const fetchData = useCallback(async () => {
    if (!enabled) return;
    setIsLoading(true);
    try {
      const result = await apiFetch<{ isMember: boolean }>("/api/calendar/membership", "GET");
      setIsMember(result?.isMember ?? false);
    } catch {
      setIsMember(false);
    } finally {
      setIsLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      setIsMember(false);
      return;
    }
    fetchData();
  }, [fetchData, enabled]);

  return {
    isLoading,
    isMember,
    refetch: fetchData,
  };
}
